import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ChevronRight, ArrowLeft, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function NewsDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/news/${id}`)
      .then(r => setArticle(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <Skeleton className="h-8 w-3/4 mb-4" /><Skeleton className="h-4 w-1/2 mb-8" />
      <Skeleton className="h-64 mb-4" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-5/6" />
    </div>
  );

  if (!article) return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
      <h1 className="text-xl font-semibold mb-4">Article Not Found</h1>
      <Link to="/news"><Button variant="outline">Back to News</Button></Link>
    </div>
  );

  return (
    <div className="page-transition">
      <section className="py-10" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link to="/" className="hover:text-[var(--brand-navy)]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/news" className="hover:text-[var(--brand-navy)]">News</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="truncate">{article.title}</span>
          </div>
        </div>
      </section>

      <section className="py-8" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            <Badge variant="secondary">{article.category === 'case-study' ? 'Case Study' : article.category === 'technical-bulletin' ? 'Technical Bulletin' : article.category === 'executive-perspective' ? 'Executive Perspective' : 'News'}</Badge>
            {article.published_at && (
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(article.published_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{article.title}</h1>
          {article.image_url && (
            <div className="rounded-lg overflow-hidden mb-8">
              <img src={article.image_url} alt={article.title} className="w-full h-auto max-h-[400px] object-cover" />
            </div>
          )}

          {/* Markdown rendered content */}
          <div className="article-content">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mt-10 mb-4 pb-2 border-b" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{children}</h2>
                ),
                h2: ({ children }) => (
                  <h3 className="text-lg sm:text-xl font-semibold tracking-tight mt-8 mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{children}</h3>
                ),
                h3: ({ children }) => (
                  <h4 className="text-base font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{children}</h4>
                ),
                h4: ({ children }) => (
                  <h5 className="text-sm font-semibold mt-4 mb-2" style={{ color: 'var(--brand-navy)' }}>{children}</h5>
                ),
                p: ({ children }) => (
                  <p className="text-sm leading-relaxed text-muted-foreground mb-4">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-outside ml-5 mb-4 space-y-1.5">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-outside ml-5 mb-4 space-y-1.5">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="text-sm text-muted-foreground leading-relaxed">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold" style={{ color: 'var(--brand-navy)' }}>{children}</strong>
                ),
                hr: () => (
                  <hr className="my-8 border-t" style={{ borderColor: 'hsl(var(--border))' }} />
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 pl-4 py-2 my-4 italic text-sm text-muted-foreground" style={{ borderColor: 'var(--brand-teal)' }}>{children}</blockquote>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full zebra-table spec-table border rounded-lg overflow-hidden">{children}</table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead style={{ background: 'var(--surface-2)' }}>{children}</thead>
                ),
                th: ({ children }) => (
                  <th className="text-left p-3 text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{children}</th>
                ),
                td: ({ children }) => (
                  <td className="p-3 text-sm text-muted-foreground border-t">{children}</td>
                ),
                code: ({ children }) => (
                  <code className="px-1.5 py-0.5 rounded text-xs font-mono" style={{ background: 'var(--surface-2)', color: 'var(--brand-teal)' }}>{children}</code>
                ),
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>

          {article.tags && article.tags.length > 0 && (
            <div className="mt-8 pt-6 border-t flex flex-wrap gap-1">
              <span className="text-xs text-muted-foreground mr-2">Tags:</span>
              {article.tags.map(tag => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
            </div>
          )}
          <div className="mt-8">
            <Link to="/news">
              <Button variant="outline" size="sm"><ArrowLeft className="w-3.5 h-3.5 mr-1" />Back to News & Case Studies</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
